// ✅ src/pages/Asset/AssetTransferEntry.jsx  (FULL FILE - UPDATED WITH LOCALSTORAGE HEADERS)
// Locks edit when:
//   data.fld_IsApproved === true  OR  data.transferIdRows[0].fld_IsApproved === true
// Also locks if inwarded (optional): inwarddate/inwardedby

import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaCheck } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate, useLocation } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/transferEntry";

// yyyy-MM-dd for <input type="date">
const fmtISO = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const getLS = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
};

const getBranchId = (b) =>
  b?.fld_Branchid ??
  b?.fld_branchid ??
  b?.branchid ??
  b?.BranchId ??
  b?.id ??
  b?.value ??
  "";

const getBranchName = (b) =>
  b?.fld_BranchName ??
  b?.fld_branchName ??
  b?.branchname ??
  b?.BranchName ??
  b?.name ??
  b?.text ??
  "";

const getTypeId = (t) =>
  t?.fld_assettypeid ??
  t?.fld_Assettypeid ??
  t?.AssetTypeId ??
  t?.assetTypeId ??
  t?.id ??
  t?.value ??
  "";

const getTypeName = (t) =>
  t?.AssetType ??
  t?.fld_Assettype ??
  t?.fld_assettype ??
  t?.AssetTypeName ??
  t?.name ??
  t?.text ??
  "";

const getSubTypeId = (s) =>
  s?.fld_assetsubtypeid ??
  s?.fld_AssetSubTypeID ??
  s?.fld_Assetsubtypeid ??
  s?.AssetSubTypeId ??
  s?.assetSubTypeId ??
  s?.id ??
  s?.value ??
  "";

const getSubTypeName = (s) =>
  s?.fld_assetsubtypename ??
  s?.fld_AssetSubTypeName ??
  s?.fld_Assetsubtype ??
  s?.AssetSubTypeName ??
  s?.name ??
  s?.text ??
  "";

const getCodeAssetId = (c) =>
  c?.fld_AssetID ?? c?.AssetID ?? c?.assetId ?? c?.id ?? c?.value ?? "";
const getCodeText = (c) =>
  c?.fld_IdCodeno ?? c?.IdCode ?? c?.idCode ?? c?.text ?? "";

const AssetTransferEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId],
  );

  const loginuserid = userId;

  // ✅ Edit ID: prefer URL ?transferId=, fallback localStorage EditTransID
  const editTransId = useMemo(() => {
    const qs = new URLSearchParams(location.search || "");
    const qid = qs.get("transferId") || qs.get("editTransId");
    if (qid && Number(qid)) return Number(qid);
    const v = localStorage.getItem("EditTransID");
    return v ? Number(v) : 0;
  }, [location.search]);

  const didRunEditRef = useRef(false);
  const didWarnLockRef = useRef(false);

  const [loading, setLoading] = useState(false);

  const [branches, setBranches] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [assetSubTypes, setAssetSubTypes] = useState([]);

  const [codes, setCodes] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState([]);

  const [detail, setDetail] = useState({
    description: "",
    make: "",
    model: "",
    serialNo: "",
    registrationNumber: "",
    size: "",
    noOfQty: "",
  });

  const [formData, setFormData] = useState({
    fromBranchId: "",
    assetTypeId: "",
    assetSubTypeId: "",
    transferDate: fmtISO(new Date()),
    toBranchId: "",
    reason: "",
    transferQty: "",
  });

  const [errors, setErrors] = useState({});

  // ✅ lock state (approved/inwarded)
  const [isLocked, setIsLocked] = useState(false);
  const [lockReason, setLockReason] = useState("");

  const handleInputChange = (field, value) => {
    if (isLocked) return;
    setFormData((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  const clearDetailsOnly = () => {
    setDetail({
      description: "",
      make: "",
      model: "",
      serialNo: "",
      registrationNumber: "",
      size: "",
      noOfQty: "",
    });
  };

  const resetSelection = () => {
    setSelectedAssetIds([]);
    setCodes([]);
    clearDetailsOnly();
  };

  const validate = () => {
    if (isLocked) return false;

    const e = {};
    if (!formData.fromBranchId) e.fromBranchId = "Transfer From is required";
    if (!formData.transferDate) e.transferDate = "Transfer Date is required";
    if (!formData.toBranchId) e.toBranchId = "To Branch is required";
    if (String(formData.toBranchId) === String(formData.fromBranchId)) {
      e.toBranchId = "From/To Branch cannot be same";
    }
    if (!String(formData.reason || "").trim()) e.reason = "Reason is required";
    if (selectedAssetIds.length === 0)
      e.idCodes = "Please select at least one Id Code";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // -----------------------------
  // API
  // -----------------------------
  const loadInit = async () => {
    const res = await axios.get(`${API_BASE}/init`, {
      headers: commonHeaders,
    });
    if (!res.data?.success) throw new Error(res.data?.message || "Init failed");
    const d = res.data.data || {};
    setBranches(d.branches || []);
    setAssetTypes(d.assetTypes || []);
    return d;
  };

  const loadSubTypes = async (assetTypeId) => {
    setAssetSubTypes([]);
    if (!assetTypeId) return [];

    const res = await axios.get(`${API_BASE}/subtypes`, {
      headers: commonHeaders,
      params: { assetTypeId },
    });

    if (!res.data?.success)
      throw new Error(res.data?.message || "Failed to load subtypes");
    const list = res.data.data || [];
    setAssetSubTypes(list);
    return list;
  };

  const loadCodes = async ({
    assetTypeId,
    assetSubTypeId,
    fromBranchId,
    preserveSelection = false,
  }) => {
    if (!assetTypeId) {
      if (!preserveSelection) resetSelection();
      return [];
    }

    if (!preserveSelection) resetSelection();

    const res = await axios.get(`${API_BASE}/codes`, {
      headers: commonHeaders,
      params: {
        assetTypeId,
        assetSubTypeId: assetSubTypeId || "%",
        fromBranchId: fromBranchId || undefined,
      },
    });

    if (!res.data?.success)
      throw new Error(res.data?.message || "Failed to load Id Codes");
    const list = res.data.data || [];
    setCodes(list);
    return list;
  };

  const loadAssetDetails = async (assetIdsCsv) => {
    if (!assetIdsCsv) {
      clearDetailsOnly();
      return [];
    }

    const res = await axios.get(`${API_BASE}/assetDetails`, {
      headers: commonHeaders,
      params: { assetIds: assetIdsCsv },
    });

    if (!res.data?.success)
      throw new Error(res.data?.message || "Failed to load details");

    const rows = res.data.data || [];
    if (rows.length === 0) {
      clearDetailsOnly();
      return [];
    }

    const joinField = (key) =>
      rows
        .map((r) => r?.[key] ?? "")
        .join(",")
        .replace(/,+$/g, "");

    const description = joinField("fld_Description");
    const make = joinField("fld_MakeName");
    const model = joinField("fld_ModelName");
    const serialNo = joinField("fld_Machinesrno");
    const size = joinField("fld_size");
    const noOfQty = joinField("fld_noofqty");

    const regCandidates = rows
      .map((r) => String(r?.fld_Description || ""))
      .filter((x) => x.startsWith("TN"));
    const registrationNumber = regCandidates.join(",") || "";

    setDetail({
      description,
      make,
      model,
      serialNo,
      registrationNumber,
      size,
      noOfQty,
    });

    return rows;
  };

  // ✅ EDIT LOAD
  const loadEdit = async (transferId) => {
    const res = await axios.get(`${API_BASE}/edit`, {
      headers: commonHeaders,
      params: { transferId },
    });

    if (!res.data?.success)
      throw new Error(res.data?.message || "Failed to load edit data");

    const d = res.data.data || {};
    const row0 = (d.transferIdRows && d.transferIdRows[0]) || {};

    // ✅ APPROVAL LOCK CHECK
    const isApproved =
      Boolean(d.fld_IsApproved) ||
      Boolean(d.is_approved) ||
      Boolean(row0.fld_IsApproved) ||
      Boolean(row0.is_approved);

    // optional inward lock
    const isInwarded =
      Boolean(row0.fld_inwarddate) ||
      Boolean(row0.fld_Inwardedby) ||
      Boolean(d.fld_inwarddate) ||
      Boolean(d.fld_Inwardedby);

    if (isApproved || isInwarded) {
      setIsLocked(true);
      setLockReason(isApproved ? "Approved" : "Inwarded");

      if (!didWarnLockRef.current) {
        didWarnLockRef.current = true;
        toast.warn(
          `Can't edit this transfer because it is ${isApproved ? "Approved" : "Inwarded"}.`,
        );
      }
    } else {
      setIsLocked(false);
      setLockReason("");
    }

    const fromBranchId = toStr(d.fromBranchId || row0.fld_Frombranchid || "");
    const toBranchId = toStr(d.toBranchId || row0.fld_Tobranchid || "");
    const reason = toStr(d.transferReason || row0.fld_TransferReason || "");
    const transferDate =
      d.transferDate || row0.fld_Transferdate
        ? fmtISO(d.transferDate || row0.fld_Transferdate)
        : fmtISO(new Date());

    const idsCsv = toStr(d.assetIds || row0.fld_Assetid || "");
    const ids = idsCsv
      ? idsCsv
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean)
      : [];

    const rows = d.assetDetails || [];
    const first = rows?.[0] || {};
    const inferredTypeId = toStr(
      first.fld_Assettypeid || first.fld_assettypeid || d.assetTypeId || "",
    );
    const inferredSubTypeId = toStr(
      first.fld_Assetsubtypeid ||
        first.fld_assetsubtypeid ||
        d.assetSubTypeId ||
        "",
    );

    setFormData((p) => ({
      ...p,
      fromBranchId,
      toBranchId,
      reason,
      transferDate,
      assetTypeId: inferredTypeId || p.assetTypeId,
      assetSubTypeId: inferredSubTypeId || "",
    }));

    if (inferredTypeId) {
      await loadSubTypes(inferredTypeId);
      await loadCodes({
        assetTypeId: inferredTypeId,
        assetSubTypeId: inferredSubTypeId || "%",
        fromBranchId,
        preserveSelection: true,
      });
    }

    setSelectedAssetIds(ids);
    await loadAssetDetails(ids.join(","));

    return d;
  };

  // -----------------------------
  // HANDLERS
  // -----------------------------
  const toggleId = (assetId) => {
    if (isLocked) return;

    const id = String(assetId);
    setSelectedAssetIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      loadAssetDetails(next.join(",")).catch((e) => toast.error(e.message));
      return next;
    });

    if (errors.idCodes) setErrors((p) => ({ ...p, idCodes: "" }));
  };

  const handleSubmit = async () => {
    if (isLocked) {
      toast.warn(`This transfer is ${lockReason}. Update not allowed.`);
      return;
    }

    if (!validate()) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        assetTypeId: formData.assetTypeId,
        assetSubTypeId: formData.assetSubTypeId,
        assetIds: selectedAssetIds,

        fromBranchId: toInt(formData.fromBranchId),
        toBranchId: toInt(formData.toBranchId),
        transferBy: toInt(loginuserid),

        transferDate: formData.transferDate,
        reason: formData.reason,
        editTransId: editTransId || 0,

        transferQty: formData.transferQty || 0,
        assetNoOfQty: detail.noOfQty || "0",

        idCodeTextCsv: selectedAssetIds
          .map((id) => {
            const row = codes.find(
              (c) => String(getCodeAssetId(c)) === String(id),
            );
            return row ? getCodeText(row) : "";
          })
          .filter(Boolean)
          .join(","),
      };

      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved Successfully");
        localStorage.removeItem("EditTransID");
        navigate("/Asset/AssetTransferListing");
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate("/Asset/AssetTransferListing");

  // -----------------------------
  // COMPUTED
  // -----------------------------
  const fromBranchName = useMemo(() => {
    const f = branches.find(
      (b) => String(getBranchId(b)) === String(formData.fromBranchId),
    );
    return f ? getBranchName(f) : "";
  }, [branches, formData.fromBranchId]);

  const toBranches = useMemo(() => {
    const fromId = String(formData.fromBranchId || "");
    return branches.filter((b) => String(getBranchId(b)) !== fromId);
  }, [branches, formData.fromBranchId]);

  // -----------------------------
  // EFFECTS
  // -----------------------------
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadInit();
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editTransId) return;
    if (branches.length === 0 && assetTypes.length === 0) return;
    if (didRunEditRef.current) return;
    didRunEditRef.current = true;

    (async () => {
      try {
        setLoading(true);
        await loadEdit(editTransId);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTransId, branches.length, assetTypes.length]);

  useEffect(() => {
    if (!formData.assetTypeId) {
      setAssetSubTypes([]);
      resetSelection();
      return;
    }
    if (isLocked) return;

    (async () => {
      try {
        await loadSubTypes(formData.assetTypeId);
        await loadCodes({
          assetTypeId: formData.assetTypeId,
          assetSubTypeId: formData.assetSubTypeId,
          fromBranchId: formData.fromBranchId,
          preserveSelection: false,
        });
      } catch (e) {
        toast.error(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.assetTypeId]);

  useEffect(() => {
    if (!formData.assetTypeId) return;
    if (isLocked) return;

    (async () => {
      try {
        await loadCodes({
          assetTypeId: formData.assetTypeId,
          assetSubTypeId: formData.assetSubTypeId,
          fromBranchId: formData.fromBranchId,
          preserveSelection: false,
        });
      } catch (e) {
        toast.error(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.assetSubTypeId]);

  useEffect(() => {
    if (
      formData.toBranchId &&
      String(formData.toBranchId) === String(formData.fromBranchId)
    ) {
      setFormData((p) => ({ ...p, toBranchId: "" }));
    }

    if (!formData.assetTypeId) return;
    if (isLocked) return;

    (async () => {
      try {
        await loadCodes({
          assetTypeId: formData.assetTypeId,
          assetSubTypeId: formData.assetSubTypeId,
          fromBranchId: formData.fromBranchId,
          preserveSelection: false,
        });
      } catch (e) {
        toast.error(e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.fromBranchId]);

  const disableAll = isLocked || loading;

  return (
    <AssetLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TRANSFER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <button
          onClick={handleBack}
         className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <FaArrowLeft />
          Back
        </button>

        <div
          className={`p-8 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1
                className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                Asset Transfer Entry
              </h1>

              {editTransId ? (
                <div
                  className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}
                >
                  Edit Transfer ID: <b>{editTransId}</b>
                </div>
              ) : null}

              {isLocked ? (
                <div className="text-xs mt-1 text-red-500 font-semibold">
                  Locked: {lockReason} (Edit disabled)
                </div>
              ) : null}
            </div>
          </div>
          {loading && (
            <div
              className={`mb-4 p-3 rounded ${isDark ? "bg-gray-800 text-gray-300" : "bg-gray-50 text-gray-700"}`}
            >
              Loading...
            </div>
          )}

          <div className="max-w-5xl mx-auto space-y-6">
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3">
                <label
                  className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Transfer From <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="col-span-9">
                <select
                  disabled={disableAll}
                  value={formData.fromBranchId}
                  onChange={(e) =>
                    handleInputChange("fromBranchId", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg ${errors.fromBranchId ? "border-red-500" : ""} ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${disableAll ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <option value="">Select</option>
                  {branches.map((b, idx) => {
                    const id = getBranchId(b);
                    const name = getBranchName(b);
                    return (
                      <option key={idx} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>

                {errors.fromBranchId && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.fromBranchId}
                  </p>
                )}
                {fromBranchName && (
                  <div
                    className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Selected From: {fromBranchName}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3">
                <label
                  className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Asset Type
                </label>
              </div>
              <div className="col-span-9">
                <select
                  disabled={disableAll}
                  value={formData.assetTypeId}
                  onChange={(e) =>
                    handleInputChange("assetTypeId", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${disableAll ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <option value="">Select</option>
                  {assetTypes.map((t, idx) => {
                    const id = getTypeId(t);
                    const name = getTypeName(t) || id;
                    return (
                      <option key={idx} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3">
                <label
                  className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Asset Sub Type
                </label>
              </div>
              <div className="col-span-9">
                <select
                  disabled={disableAll}
                  value={formData.assetSubTypeId}
                  onChange={(e) =>
                    handleInputChange("assetSubTypeId", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${disableAll ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <option value="">Select</option>
                  {assetSubTypes.map((s, idx) => {
                    const id = getSubTypeId(s);
                    const name = getSubTypeName(s) || id;
                    return (
                      <option key={idx} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 items-start">
              <div className="col-span-3">
                <label
                  className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Id Code <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="col-span-9">
                <div
                  className={`border rounded-lg p-3 max-h-56 overflow-auto ${
                    isDark
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-300"
                  }`}
                >
                  {codes.length === 0 ? (
                    <div
                      className={`${isDark ? "text-gray-400" : "text-gray-500"}`}
                    >
                      No Id Codes
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {codes.map((c, idx) => {
                        const id = getCodeAssetId(c);
                        const text = getCodeText(c);
                        const checked = selectedAssetIds.includes(String(id));
                        return (
                          <label
                            key={idx}
                            className={`flex items-center gap-2 ${
                              disableAll
                                ? "cursor-not-allowed opacity-80"
                                : "cursor-pointer"
                            }`}
                          >
                            <input
                              type="checkbox"
                              disabled={disableAll}
                              checked={checked}
                              onChange={() => toggleId(id)}
                              className="h-4 w-4"
                            />
                            <span
                              className={`${isDark ? "text-gray-200" : "text-gray-700"}`}
                            >
                              {text}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                {errors.idCodes && (
                  <p className="text-red-500 text-sm mt-1">{errors.idCodes}</p>
                )}
                <div
                  className={`text-xs mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  Selected: {selectedAssetIds.length}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["Description", detail.description],
                ["Make", detail.make],
                ["Model", detail.model],
                ["Serial No", detail.serialNo],
                ["Registration No", detail.registrationNumber],
                ["Size", detail.size],
                ["No of Qty", detail.noOfQty],
              ].map(([label, value], idx) => (
                <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-4">
                    <label
                      className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                    >
                      {label}
                    </label>
                  </div>
                  <div className="col-span-8">
                    <input
                      value={value || ""}
                      disabled
                      className={`w-full px-4 py-3 border rounded-lg ${
                        isDark
                          ? "bg-gray-600 border-gray-600 text-gray-200"
                          : "bg-gray-100 border-gray-300 text-gray-700"
                      }`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3">
                <label
                  className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Transfer Date <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="col-span-9">
                <input
                  disabled={disableAll}
                  type="date"
                  value={formData.transferDate}
                  onChange={(e) =>
                    handleInputChange("transferDate", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg ${errors.transferDate ? "border-red-500" : ""} ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${disableAll ? "opacity-70 cursor-not-allowed" : ""}`}
                />
                {errors.transferDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.transferDate}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-3">
                <label
                  className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Transfer To <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="col-span-9">
                <select
                  disabled={disableAll}
                  value={formData.toBranchId}
                  onChange={(e) =>
                    handleInputChange("toBranchId", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg ${errors.toBranchId ? "border-red-500" : ""} ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${disableAll ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <option value="">Select</option>
                  {toBranches.map((b, idx) => {
                    const id = getBranchId(b);
                    const name = getBranchName(b);
                    return (
                      <option key={idx} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                {errors.toBranchId && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.toBranchId}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-4 items-start">
              <div className="col-span-3">
                <label
                  className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  Reason <span className="text-red-500">*</span>
                </label>
              </div>
              <div className="col-span-9">
                <textarea
                  disabled={disableAll}
                  rows={3}
                  value={formData.reason}
                  onChange={(e) => handleInputChange("reason", e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg resize-none ${errors.reason ? "border-red-500" : ""} ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${disableAll ? "opacity-70 cursor-not-allowed" : ""}`}
                />
                {errors.reason && (
                  <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
                )}
              </div>
            </div>

            {!isLocked && (
              <div className="flex justify-end pt-6">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                >
                  <FaCheck />
                  {editTransId ? "Update" : "Submit"}
                </button>
              </div>
            )}

            {isLocked && (
              <div className="flex justify-center pt-6">
                <div
                  className={`px-4 py-3 rounded-lg border ${
                    isDark
                      ? "border-gray-700 bg-gray-800 text-gray-200"
                      : "border-gray-200 bg-gray-50 text-gray-700"
                  }`}
                >
                  Update not allowed (Transfer is {lockReason}).
                </div>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTransferEntry;
